import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import Button from '../../components/ui/Button';
import PracticeAreaCard from './components/PracticeAreaCard';
import { legalAreasService } from '../../services/legalAreasService';


const LegalAreasLanding = () => {
  const [activeFilters, setActiveFilters] = useState({});
  const [selectedArea, setSelectedArea] = useState('contracts');
  const [showCalculator, setShowCalculator] = useState(false);

  // Replace hardcoded data with Supabase state
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Icon mapping for database areas
  const iconMapping = {
    'Vertragsrecht': 'FileText',
    'Schuldenrecht': 'CreditCard',
    'Urheberrecht': 'Copyright',
    'Strafrecht': 'Shield',
    'Familienrecht': 'Heart',
    'Arbeitsrecht': 'Briefcase',
    'Mietrecht': 'Home',
    'Verkehrsrecht': 'Car'
  };

  // Pricing mapping (can be moved to database later)
  const pricingMapping = {
    'Vertragsrecht': { price: '2,99', successRate: 94, isPopular: true },
    'Schuldenrecht': { price: '2,99', successRate: 89, isPopular: false },
    'Urheberrecht': { price: '2,99', successRate: 91, isPopular: false },
    'Strafrecht': { price: '2,99', successRate: 87, isPopular: false },
    'Familienrecht': { price: '2,99', successRate: 92, isPopular: false },
    'Arbeitsrecht': { price: '2,99', successRate: 90, isPopular: false },
    'Mietrecht': { price: '2,99', successRate: 93, isPopular: false },
    'Verkehrsrecht': { price: '2,99', successRate: 88, isPopular: false }
  };

  // Load legal areas from Supabase
  useEffect(() => {
    const loadLegalAreas = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await legalAreasService?.getAllAreas();

        if (fetchError) {
          setError(fetchError);
          setPracticeAreas([]); // Set empty array on error
          return;
        }

        // Transform database data to match UI expectations
        const transformedAreas = data?.map((area) => {
          const pricing = pricingMapping?.[area?.name] || { price: '3,99', successRate: 90, isPopular: false };

          return {
            id: area?.id,
            icon: iconMapping?.[area?.name] || 'FileText',
            title: area?.name || 'Legal Area',
            description: area?.description || 'Professional legal consultation',
            features: area?.description?.split('.')?.slice(0, 5)?.map((sentence) => sentence?.trim())?.filter(Boolean) || [
            'Professional consultation',
            'Expert legal advice',
            'Case assessment',
            'Document review',
            'Legal representation'],

            consultationPrice: pricing?.price || '3,99',
            successRate: pricing?.successRate || 90,
            caseExample: `Professional legal consultation in ${area?.name || 'this area'} with successful resolution of client matters.`,
            isPopular: pricing?.isPopular || false,
            created_at: area?.created_at
          };
        }) || [];

        setPracticeAreas(transformedAreas);
      } catch (error) {
        setError('An unexpected error occurred while loading legal areas.');
        setPracticeAreas([]);
      } finally {
        setLoading(false);
      }
    };

    loadLegalAreas();
  }, []);

  const filteredAreas = practiceAreas?.filter((area) => {
    // Apply filters logic here
    return true; // Simplified for this example
  });

  const handleAreaSelect = (areaTitle) => {
    const areaMap = {
      'Vertragsrecht': 'contracts',
      'Schuldenrecht': 'debt',
      'Urheberrecht': 'ip',
      'Strafrecht': 'criminal',
      'Familienrecht': 'family',
      'Arbeitsrecht': 'employment',
      'Mietrecht': 'rental',
      'Verkehrsrecht': 'traffic'
    };
    setSelectedArea(areaMap?.[areaTitle] || 'contracts');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Rechtsbereiche werden geladen...</p>
          </div>
        </div>
      </div>);

  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-xl font-bold text-text-primary mb-2">Fehler beim Laden</h2>
            <p className="text-text-secondary mb-4">{error}</p>
            <Button
              onClick={() => window.location?.reload()}
              variant="outline"
              className="mr-2">

              Erneut versuchen
            </Button>
            <Link to="/contact">
              <Button variant="default">
                Direkt kontaktieren
              </Button>
            </Link>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <Helmet>
        <title>AdvoCall Professional - Sofortige Rechtsberatung per Telefon | 24/7 verfügbar</title>
        <meta
          name="description"
          content="Professionelle Rechtsberatung per Telefon - 24/7 verfügbar. Transparente Preise ab 2,99 €/Min. Sofortige Hilfe von zertifizierten Anwälten. Jetzt anrufen!" />

        <meta name="keywords" content="Rechtsberatung, Telefon, Anwalt, 24/7, sofort, günstig, transparent, AdvoCall" />
        <meta property="og:title" content="AdvoCall Professional - Sofortige Rechtsberatung per Telefon" />
        <meta property="og:description" content="Professionelle Rechtsberatung per Telefon - 24/7 verfügbar. Transparente Preise ab 2,99 €/Min." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://advocall.de" />
        <link rel="canonical" href="https://advocall.de" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            "name": "AdvoCall Professional",
            "description": "Professionelle Rechtsberatung per Telefon - 24/7 verfügbar",
            "url": "https://advocall.de",
            "telephone": "09001000312",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Kurfürstendamm 14",
              "postalCode": "10719",
              "addressLocality": "Berlin",
              "addressCountry": "DE"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "5000"
            },
            "priceRange": "€€",
            "availableLanguage": "German"
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="relative">
          {/* Emergency Banner */}
          <div className="relative z-20 bg-error text-white py-3">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center space-x-3">
                <Icon name="AlertTriangle" size={20} className="animate-pulse" />
                <span className="text-sm font-medium">
                  Rechtlicher Notfall? Sofortige Beratung verfügbar - 24/7
                </span>
                <a href="tel:09001000312">
                  <Button
                    variant="outline"
                    size="xs"
                    className="bg-white text-error border-white hover:bg-error hover:text-white ml-4">
                    Jetzt anrufen
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Hero Section */}
          <section className="relative min-h-screen bg-gradient-to-br from-primary via-brand-primary to-accent overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            </div>

            {/* Hero Content */}
            <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
              <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
                {/* Left Content */}
                <div className="text-white space-y-8">
                  <div className="space-y-4">
                    <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                      <Icon name="Shield" size={16} />
                      <span className="text-sm font-medium">ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH</span>
                    </div>
                    
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                      Rechtsberatung per Telefon
                      <span className="block text-conversion-accent">wenn Sie sie am meisten brauchen</span>
                    </h1>
                    
                    <p className="text-xl text-white/90 leading-relaxed max-w-2xl">
                      Professionelle Rechtsberatung per Telefon - 24/7 verfügbar. 
                      Transparente Preise, sofortige Hilfe, keine Wartezeiten.
                    </p>
                  </div>

                  {/* Dual Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a href="tel:09001000312">
                      <Button
                        variant="default"
                        size="lg"
                        iconName="Phone"
                        iconPosition="left"
                        className="bg-conversion-accent hover:bg-cta text-white shadow-elevated cta-button text-lg px-8 py-4 w-full sm:w-auto">
                        09001000312 - 2,99 €/Min
                      </Button>
                    </a>
                    
                    <Link to="/contact">
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="Calendar"
                        iconPosition="left"
                        className="border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-4 w-full sm:w-auto">
                        Termin vereinbaren
                      </Button>
                    </Link>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-6 pt-8">
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={20} className="text-trust-builder" />
                      <span className="text-sm">Kurze Wartezeit</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Star" size={20} className="text-yellow-400" />
                      <span className="text-sm">Top Kundenbewertung</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={20} className="text-trust-builder" />
                      <span className="text-sm">Viele zufriedene Kunden</span>
                    </div>
                  </div>
                </div>

                {/* Right Content - Legal Areas Preview */}
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-elevated">
                    <h3 className="text-2xl font-bold text-white mb-6 text-center">
                      Unsere Rechtsbereiche
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {practiceAreas?.slice(0, 4)?.map((area, index) =>
                      <div key={area?.id || index} className="bg-white/10 rounded-lg p-4 text-center">
                          <Icon name={area?.icon} size={24} className="text-white mx-auto mb-2" />
                          <div className="text-white text-sm font-medium">{area?.title}</div>
                          <div className="text-white/70 text-xs">ab {area?.consultationPrice} €/Min</div>
                        </div>
                      )}
                    </div>

                    <Link to="/legal-areas">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="right"
                        className="border-white text-white hover:bg-white hover:text-primary w-full">
                        Alle Bereiche ansehen
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Floating Stats */}
                  <div className="absolute -top-4 -right-4 bg-trust-builder text-white rounded-lg p-4 shadow-elevated">
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-xs">Verfügbar</div>
                  </div>
                  
                  <div className="absolute -bottom-4 -left-4 bg-conversion-accent text-white rounded-lg p-4 shadow-elevated">
                    <div className="text-2xl font-bold">2,99€</div>
                    <div className="text-xs">pro Minute</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
              <div className="animate-bounce">
                <Icon name="ChevronDown" size={32} className="text-white/60" />
              </div>
            </div>
          </section>

          {/* Statistics Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  Vertrauen durch Qualität
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Viele zufriedene Kunden vertrauen auf unsere professionelle Rechtsberatung
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                { icon: 'Users', value: 'Viele', label: 'Zufriedene Mandanten', color: 'text-trust-builder' },
                { icon: 'Clock', value: 'Kurz', label: 'Durchschnittliche Wartezeit', color: 'text-primary' },
                { icon: 'Award', value: 'Top', label: 'Google Bewertung', color: 'text-yellow-500' },
                { icon: 'Shield', value: '24/7', label: 'Verfügbarkeit', color: 'text-success' }]?.
                map((stat, index) =>
                <div key={index} className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 ${stat?.color?.replace('text-', 'bg-')}/10 rounded-full mb-4`}>
                      <Icon name={stat?.icon} size={32} className={stat?.color} />
                    </div>
                    <div className="text-3xl font-bold text-text-primary mb-2">{stat?.value}</div>
                    <div className="text-text-secondary">{stat?.label}</div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Services Section - Now with Supabase Data */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  Unsere Rechtsbereiche
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Professionelle Beratung in allen wichtigen Rechtsbereichen - sofort verfügbar
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {practiceAreas?.length > 0 ?
                practiceAreas?.map((area, index) =>
                <div key={area?.id || index} onClick={() => handleAreaSelect(area?.title)}>
                      <PracticeAreaCard {...area} />
                    </div>
                ) :

                <div className="col-span-full text-center py-8">
                    <Icon name="AlertCircle" size={48} className="text-text-secondary mx-auto mb-4" />
                    <p className="text-text-secondary">Momentan sind keine Rechtsbereiche verfügbar.</p>
                  </div>
                }
              </div>

              <div className="text-center">
                <Link to="/legal-areas">
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="text-lg px-8 py-4">
                    Alle Bereiche im Detail
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  So einfach funktioniert's
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  In nur 3 Schritten zur professionellen Rechtsberatung
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                {
                  step: '01',
                  icon: 'Phone',
                  title: 'Anrufen',
                  description: 'Wählen Sie unsere Hotline 09001000312 und werden Sie sofort mit einem Fachanwalt verbunden.'
                },
                {
                  step: '02',
                  icon: 'MessageSquare',
                  title: 'Schildern',
                  description: 'Schildern Sie Ihr rechtliches Problem. Unsere Anwälte hören aufmerksam zu und stellen gezielte Fragen.'
                },
                {
                  step: '03',
                  icon: 'CheckCircle',
                  title: 'Lösung erhalten',
                  description: 'Erhalten Sie sofortige rechtliche Einschätzung und konkrete Handlungsempfehlungen von unseren Experten.'
                }]?.
                map((step, index) =>
                <div key={index} className="relative text-center">
                    <div className="bg-primary/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                      <Icon name={step?.icon} size={32} className="text-primary" />
                    </div>
                    
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 -translate-y-2 bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                      {step?.step}
                    </div>
                    
                    <h3 className="text-xl font-bold text-text-primary mb-4">{step?.title}</h3>
                    <p className="text-text-secondary">{step?.description}</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary via-brand-primary to-accent relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 right-10 w-80 h-80 bg-white rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                  Rechtliche Hilfe kann nicht warten
                </h2>
                <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                  Professionelle Rechtsberatung, wenn Sie sie brauchen. 
                  Wählen Sie Ihren bevorzugten Weg zur rechtlichen Lösung.
                </p>
              </div>

              {/* Contact Methods */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                {[
                {
                  icon: 'Phone',
                  title: 'Sofort anrufen',
                  subtitle: 'Direkter Kontakt zu unseren Anwälten',
                  price: '2,99 €/Min',
                  description: 'Für dringende rechtliche Fragen',
                  buttonText: 'Jetzt anrufen',
                  action: 'tel:09001000312'
                },
                {
                  icon: 'Calendar',
                  title: 'Termin vereinbaren',
                  subtitle: 'Geplante Beratung zu Ihrem Wunschtermin',
                  price: 'Ab 50 €',
                  description: 'Für ausführliche Beratungsgespräche',
                  buttonText: 'Termin buchen',
                  action: '/contact'
                }]?.
                map((method, index) =>
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                    <div className="text-center space-y-6">
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full">
                        <Icon name={method?.icon} size={40} className="text-white" />
                      </div>

                      <div className="space-y-3">
                        <h3 className="text-2xl font-bold text-white">{method?.title}</h3>
                        <p className="text-white/80">{method?.subtitle}</p>
                        <div className="text-3xl font-bold text-white">{method?.price}</div>
                        <p className="text-white/70 text-sm">{method?.description}</p>
                      </div>

                      <div className="pt-4">
                        {method?.action?.startsWith('tel:') ?
                      <a href={method?.action}>
                            <Button
                          variant="default"
                          size="lg"
                          iconName={method?.icon}
                          iconPosition="left"
                          className="bg-conversion-accent hover:bg-cta text-white cta-button w-full text-lg py-4">
                              {method?.buttonText}
                            </Button>
                          </a> :

                      <Link to={method?.action}>
                            <Button
                          variant="outline"
                          size="lg"
                          iconName={method?.icon}
                          iconPosition="left"
                          className="border-white text-white hover:bg-white hover:text-primary w-full text-lg py-4">
                              {method?.buttonText}
                            </Button>
                          </Link>
                      }
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Trust Indicators */}
              <div className="text-center space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Icon name="Users" size={32} className="text-white mx-auto" />
                    <div className="text-2xl font-bold text-white">Viele</div>
                    <div className="text-white/80 text-sm">Zufriedene Kunden</div>
                  </div>
                  
                  <div className="space-y-2">
                    <Icon name="Award" size={32} className="text-white mx-auto" />
                    <div className="text-2xl font-bold text-white">Top</div>
                    <div className="text-white/80 text-sm">Google Bewertung</div>
                  </div>
                  
                  <div className="space-y-2">
                    <Icon name="Clock" size={32} className="text-white mx-auto" />
                    <div className="text-2xl font-bold text-white">24/7</div>
                    <div className="text-white/80 text-sm">Verfügbarkeit</div>
                  </div>
                </div>

                {/* Legal Notice */}
                <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto">
                  <p className="text-white/70 text-sm leading-relaxed">
                    AdvoCall ist eine Marke der ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH. 
                    Alle Beratungen werden von zugelassenen Rechtsanwälten durchgeführt. 
                    Preise verstehen sich zzgl. der Verbindungskosten Ihres Telefonanbieters.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Accessibility Skip Link */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white px-4 py-2 rounded-lg z-50">
          Zum Hauptinhalt springen
        </a>
      </div>
    </>
  );

};

export default LegalAreasLanding;